import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { EMPTY, mergeMap } from 'rxjs';

import { DocsActions } from '../docs/docs.actions';
import { selectActiveProjectId } from '../workspace/workspace.selectors';
import { ReposActions } from './repos.actions';
import { compactChatMessages, compactReposState } from './repos.models';
import { selectReposSettings, selectReposState } from './repos.selectors';

const REPOS_KEYS = ['repos.chat', 'repos.settings', 'repos.state', 'define.definition'] as const;

function writeSettings(pid: string, settings: any) {
  return [
    DocsActions.applyLocalData({ projectId: pid, key: 'repos.settings', data: settings }),
    DocsActions.saveDoc({ projectId: pid, key: 'repos.settings', data: settings }),
  ];
}

function writeState(pid: string, state: any) {
  const data = compactReposState(state);
  return [
    DocsActions.applyLocalData({ projectId: pid, key: 'repos.state', data }),
    DocsActions.saveDoc({ projectId: pid, key: 'repos.state', data }),
  ];
}

export const loadRepos$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ReposActions.load),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      mergeMap(([, pid]) => (pid ? REPOS_KEYS.map((key) => DocsActions.loadDoc({ projectId: pid, key })) : EMPTY))
    ),
  { functional: true }
);

const setChatMessages$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ReposActions.setChatMessages),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      mergeMap(([{ messages, projectId }, activePid]) => {
        const pid = projectId ?? activePid;
        if (!pid) return EMPTY;
        const data = { messages: compactChatMessages(messages) };
        return [
          DocsActions.applyLocalData({ projectId: pid, key: 'repos.chat', data }),
          DocsActions.saveDoc({ projectId: pid, key: 'repos.chat', data }),
        ];
      })
    ),
  { functional: true }
);

export const saveSettings$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ReposActions.saveSettings),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      mergeMap(([{ settings, projectId }, activePid]) => {
        const pid = projectId ?? activePid;
        return pid ? writeSettings(pid, settings) : EMPTY;
      })
    ),
  { functional: true }
);

export const patchSettings$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ReposActions.patchSettings),
      concatLatestFrom(() => [store.select(selectActiveProjectId), store.select(selectReposSettings)]),
      mergeMap(([{ patch, projectId }, activePid, current]) => {
        const pid = projectId ?? activePid;
        if (!pid) return EMPTY;
        const next = {
          github: {
            installationId: patch?.github?.installationId ?? current.github.installationId,
            repos: patch?.github?.repos ?? current.github.repos ?? [],
          },
        };
        return writeSettings(pid, next);
      })
    ),
  { functional: true }
);

export const saveState$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ReposActions.saveState),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      mergeMap(([{ state, projectId }, activePid]) => {
        const pid = projectId ?? activePid;
        return pid ? writeState(pid, state) : EMPTY;
      })
    ),
  { functional: true }
);

export const patchState$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ReposActions.patchState),
      concatLatestFrom(() => [store.select(selectActiveProjectId), store.select(selectReposState)]),
      mergeMap(([{ patch, projectId }, activePid, current]) => {
        const pid = projectId ?? activePid;
        return pid ? writeState(pid, { ...current, ...patch }) : EMPTY;
      })
    ),
  { functional: true }
);

export const reposEffects = {
  loadRepos$,
  setChatMessages$,
  saveSettings$,
  patchSettings$,
  saveState$,
  patchState$,
};
